import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { IconType } from 'react-icons';
import {
    IoAddOutline,
    IoCheckmarkSharp,
    IoChevronBack,
    IoChevronForward,
    IoCubeOutline,
    IoGlobeOutline,
    IoImageOutline,
} from 'react-icons/io5';

import {
    ActionIcon,
    Anchor,
    AnchorProps,
    Badge,
    Flex,
    Group,
    Highlight,
    List,
    ListItem,
    Stack,
    Text,
    ThemeIcon,
    Tooltip,
} from '@mantine/core';
import type { EmblaCarouselType } from 'embla-carousel';

import { all, CompanyProfile, ComponentType, currentConverter, powerConverter, voltageConverter } from 'models';
import { FormatHelpers } from 'helpers/formatters';
import { RouterHelpers } from 'helpers/RouterHelpers';

import { ProfileCard } from 'components/company-profile/components/ProfileCard';
import { ProfileModal } from 'components/company-profile/components/ProfileModal';
import { SparseSectionPanel, useIsSparseSection } from 'components/company-profile/components/SparseSectionPanel';
import { ProductSeriesHelpers } from 'components/company-profile/helpers/ProductSeriesHelpers';
import { ProfileSectionHelpers } from 'components/company-profile/helpers/ProfileSectionHelpers';
import { ComponentIcon } from 'components/component-icons/ComponentIcon';
import { IKImage } from 'components/ik-image/IKImage';
import { CarouselSection } from 'components/section/CarouselSection';
import { Section, SectionProps } from 'components/section/Section';
import { useWorkspaceConfig } from 'hooks/use-workspace-config';
import { InternalTrackingService } from 'services/InternalTrackingService';

import cx from './ProductSeries.module.scss';

const InlineIcon = ({ icon: Icon }: { icon: IconType }) => (
    <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: 8 }}>
        <Icon size={18} />
    </span>
);

const ProductSeries = ({
    company,
    title = '',
    filterable = true,
    ...props
}: SectionProps & {
    company: CompanyProfile;
    title?: string;
    filterable?: boolean;
}) => {
    const productSeries = company.productSeries || [];

    const [selectedType, setSelectedType] = useState<ComponentType | null>(null);
    const [modalOpenIndex, setModalOpenIndex] = useState<number | null>(null);
    const [embla, setEmbla] = useState<EmblaCarouselType | null>(null);

    const router = useRouter();
    const { workspace: inWorkspace, homepagePath } = useWorkspaceConfig();
    const productSeriesNameFromQuery = router.query.productSeries as string | undefined;

    const availableTypes = useMemo(() => ProductSeriesHelpers.getAvailableTypes(productSeries), [productSeries]);

    const filteredProductSeries = useMemo(
        () =>
            ProductSeriesHelpers.filterProductSeries(
                productSeries,
                selectedType,
                '',
                company.productSeriesCustomSortOrder ?? false,
            ),
        [productSeries, selectedType, company.productSeriesCustomSortOrder],
    );
    const isSparse = useIsSparseSection(filteredProductSeries.length);

    useEffect(() => {
        if (!productSeriesNameFromQuery) return;

        const index = ProductSeriesHelpers.findIndexBySlug(filteredProductSeries, productSeriesNameFromQuery);
        if (index === -1) return;

        setModalOpenIndex(index);
        ProductSeriesHelpers.scrollEmblaToIndex(embla, index);
        ProductSeriesHelpers.scrollProductSeriesIntoView();
    }, [productSeriesNameFromQuery, filteredProductSeries, embla, router]);

    const handleOpenModal = (index: number | null) => {
        setModalOpenIndex(index);

        if (index === null && productSeriesNameFromQuery) {
            const { productSeries: _ps, ...restQuery } = router.query;
            router.replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true });
        }
    };

    if (!productSeries.length) {
        return null;
    }

    const sparsePanel = isSparse && (
        <SparseSectionPanel
            key="sparse-panel"
            owner={{
                icon: <IoAddOutline />,
                title: 'Add a product series',
                description: 'Group related products so buyers can compare your range.',
                cta: 'Add product series',
                href: RouterHelpers.urls.profiles.edit(company.slug, 'product-series'),
            }}
            visitor={{
                icon: <IoCubeOutline />,
                title: 'Looking for more?',
                description: 'Browse the full product catalog to compare options.',
                cta: 'Browse catalog',
                href: (inWorkspace && homepagePath) || RouterHelpers.urls.products.overview(),
            }}
        />
    );

    const aiBadge = company.internal && (
        <Tooltip label="AI-generated from public sources. May contain errors." withArrow>
            <Badge className={cx.badge} size="sm" radius="sm" variant="light" color="gray">
                AI Generated
            </Badge>
        </Tooltip>
    );

    return (
        <>
            <Section
                title={
                    title && (
                        <>
                            {title}
                            {aiBadge}
                        </>
                    )
                }
                id="product-series"
                {...props}
            >
                {!title && aiBadge}
                {filterable && availableTypes.types.length > 1 && (
                    <Group gap="md" className={cx.typeFilters}>
                        {availableTypes.types.map((type) => (
                            <Anchor
                                key={type}
                                component="button"
                                type="button"
                                size="md"
                                fw={500}
                                c={selectedType === type ? 'black' : 'dimmed'}
                                // The app-wide anchor underline is nearly invisible; make it solid for the active one.
                                style={selectedType === type ? { textDecorationColor: 'currentColor' } : undefined}
                                onClick={() => {
                                    if (selectedType === type) {
                                        setSelectedType(null);
                                        return;
                                    }
                                    setSelectedType(type);
                                }}
                            >
                                {all[type]?.name || type}{' '}
                                <Badge
                                    size="xs"
                                    variant={selectedType === type ? 'filled' : 'light'}
                                    color={selectedType === type ? 'black' : undefined}
                                    ml={8}
                                >
                                    {availableTypes.typeCounts[type]}
                                </Badge>
                            </Anchor>
                        ))}
                    </Group>
                )}

                <CarouselSection cols={ProfileSectionHelpers.cols} getEmblaApi={setEmbla}>
                    {filteredProductSeries.map((series, index) => (
                        <ProductSeriesCard
                            key={series.id}
                            index={index}
                            series={series}
                            onOpenModal={handleOpenModal}
                        />
                    ))}
                    {sparsePanel}
                </CarouselSection>
            </Section>

            {modalOpenIndex !== null && (
                <ProductSeriesModal
                    openIndex={modalOpenIndex}
                    productSeries={filteredProductSeries}
                    onOpenModal={handleOpenModal}
                    searchTerm=""
                />
            )}
        </>
    );
};

const ProductSeriesCard = ({
    series,
    index,
    searchTerm = '',
    onOpenModal,
}: {
    series: CompanyProfile['productSeries'][0];
    index: number;
    searchTerm?: string;
    onOpenModal: (index: number) => void;
}) => {
    const { type, seriesName, productSeriesImage, voltage, power, current, description, distinguishingFeatures } =
        series;

    const specifications = [
        FormatHelpers.formatMinNomMax(voltage, voltageConverter),
        FormatHelpers.formatMinNomMax(power, powerConverter),
        FormatHelpers.formatMinNomMax(current, currentConverter),
    ].filter(Boolean);

    return (
        <ProfileCard
            onClick={() => onOpenModal(index)}
            imageFit="contain"
            image={
                productSeriesImage ? (
                    <IKImage fileOrId={productSeriesImage} alt={seriesName || 'Product Series'} width={500} />
                ) : all[type as ComponentType] ? (
                    <Flex w={64} align="center" opacity={0.2} mx="auto">
                        <ComponentIcon type={type as ComponentType} />
                    </Flex>
                ) : (
                    <IoImageOutline size={64} opacity={0.2} />
                )
            }
            eyebrow={type ? (all[type as ComponentType]?.name ?? type) : undefined}
            eyebrowIcon={all[type as ComponentType] && <ComponentIcon type={type as ComponentType} />}
            title={
                <Highlight inherit highlight={searchTerm}>
                    {seriesName || 'Product series'}
                </Highlight>
            }
            meta={
                specifications.length > 0 && (
                    <Text inherit fw={600}>
                        {specifications.join(' · ')}
                    </Text>
                )
            }
            description={description || distinguishingFeatures?.join(' · ')}
        />
    );
};

const ProductSeriesContent = ({
    series,
    searchTerm,
}: {
    series: CompanyProfile['productSeries'][0];
    searchTerm: string;
}) => {
    const { description, voltage, power, current, models, productNames, distinguishingFeatures } = series;

    const formattedVoltage = FormatHelpers.formatMinNomMax(voltage, voltageConverter);
    const formattedPower = FormatHelpers.formatMinNomMax(power, powerConverter);
    const formattedCurrent = FormatHelpers.formatMinNomMax(current, currentConverter);

    const specifications = [
        {
            label: 'Voltage',
            value: formattedVoltage,
        },
        {
            label: 'Power',
            value: formattedPower,
        },
        {
            label: 'Current',
            value: formattedCurrent,
        },
    ].filter(({ value }) => !!value);

    return (
        <>
            {specifications.length > 0 && (
                <Group gap="md">
                    {specifications.map(({ label, value }) => (
                        <SpecificationItem key={label} label={label} value={value} searchTerm={searchTerm} />
                    ))}
                </Group>
            )}

            {!!distinguishingFeatures?.length && (
                <List fz="inherit" pl={0} spacing={6}>
                    {distinguishingFeatures.map((feature, idx) => (
                        <ListItem
                            key={idx}
                            icon={
                                <ThemeIcon color="green" size="sm" variant="light" radius={99}>
                                    <IoCheckmarkSharp />
                                </ThemeIcon>
                            }
                            className={cx.listItem}
                        >
                            <Highlight inherit highlight={searchTerm}>
                                {feature}
                            </Highlight>
                        </ListItem>
                    ))}
                </List>
            )}

            {description && (
                <Text inherit>
                    <Highlight inherit highlight={searchTerm}>
                        {description}
                    </Highlight>
                </Text>
            )}

            {!!models?.length && (
                <Stack gap={4}>
                    <Text inherit fw={700}>
                        Product variants
                    </Text>
                    <List fz="inherit" spacing={6}>
                        {models.map((model, idx) => (
                            <ListItem key={idx}>
                                <Highlight inherit highlight={searchTerm}>
                                    {model}
                                </Highlight>
                            </ListItem>
                        ))}
                    </List>
                </Stack>
            )}

            {!!productNames?.length && (
                <Stack gap={4}>
                    <Text inherit fw={700}>
                        Also known as
                    </Text>
                    <List fz="inherit" spacing={6}>
                        {productNames.map((model, idx) => (
                            <ListItem key={idx}>
                                <Highlight inherit highlight={searchTerm}>
                                    {model}
                                </Highlight>
                            </ListItem>
                        ))}
                    </List>
                </Stack>
            )}
        </>
    );
};

const ProductSeriesWebsite = ({ url, ...props }: { url: string } & AnchorProps) => {
    return (
        <Anchor
            mt="auto"
            fz="lg"
            fw={500}
            c="black"
            href={url}
            {...InternalTrackingService.link('company.productSeries.website')}
            target="_blank"
            rel="noopener noreferrer"
            {...props}
        >
            <InlineIcon icon={IoGlobeOutline} />
            Visit website
        </Anchor>
    );
};

const SpecificationItem = ({
    label,
    value,
    searchTerm = '',
}: {
    label: string;
    value: string;
    searchTerm?: string;
}) => (
    <Stack gap={0} mr="md">
        <Text tt="uppercase" fz={10} fw={600} c="dimmed">
            {label}
        </Text>
        <Text fz="lg" fw={800}>
            <Highlight inherit highlight={searchTerm}>
                {value}
            </Highlight>
        </Text>
    </Stack>
);

const ProductSeriesModal = ({
    searchTerm,
    openIndex,
    productSeries,
    onOpenModal,
}: {
    searchTerm: string;
    openIndex: number;
    productSeries: CompanyProfile['productSeries'];
    onOpenModal: (index: number | null) => void;
}) => {
    const previousIndex = openIndex > 0 ? openIndex - 1 : productSeries.length - 1;
    const nextIndex = openIndex < productSeries.length - 1 ? openIndex + 1 : 0;

    const series = productSeries[openIndex];
    const { productSeriesImage, seriesName, productSeriesUrl, type } = series;

    return (
        <>
            {productSeries.length > 1 && (
                <Tooltip label={productSeries[previousIndex].seriesName || 'Previous'} position="left">
                    <ActionIcon
                        variant="white"
                        size="xl"
                        radius="xl"
                        className={`${cx.navArrow} ${cx.left}`}
                        onClick={() => onOpenModal(previousIndex)}
                        visibleFrom="md"
                    >
                        <IoChevronBack size={24} />
                    </ActionIcon>
                </Tooltip>
            )}

            <ProfileModal
                opened
                onClose={() => onOpenModal(null)}
                eyebrow={type ? (all[type as ComponentType]?.name ?? type) : 'Product series'}
                eyebrowIcon={all[type as ComponentType] && <ComponentIcon type={type as ComponentType} />}
                title={
                    <Highlight inherit highlight={searchTerm}>
                        {seriesName || 'Product series'}
                    </Highlight>
                }
            >
                <Stack gap="lg">
                    {productSeriesImage && (
                        <div className={cx.modalImage}>
                            <IKImage
                                fileOrId={productSeriesImage}
                                alt={seriesName || 'Product Series'}
                                width={800}
                                fit="contain"
                            />
                        </div>
                    )}

                    <ProductSeriesContent series={series} searchTerm={searchTerm} />

                    {productSeriesUrl && (
                        <div>
                            <ProductSeriesWebsite url={productSeriesUrl} />
                        </div>
                    )}
                </Stack>
            </ProfileModal>

            {productSeries.length > 1 && (
                <Tooltip label={productSeries[nextIndex].seriesName || 'Next'} position="right">
                    <ActionIcon
                        variant="white"
                        size="xl"
                        radius="xl"
                        className={`${cx.navArrow} ${cx.right}`}
                        onClick={() => onOpenModal(nextIndex)}
                        visibleFrom="md"
                    >
                        <IoChevronForward size={24} />
                    </ActionIcon>
                </Tooltip>
            )}
        </>
    );
};

export { ProductSeries };
